/**
 * Age in whole years.
 *
 * WHY THIS EXISTS
 * Eight places computed age as `moment().year() - moment.unix(dateOfBirth).year()` — a pure year
 * subtraction that ignores whether the birthday has occurred yet. That over-states the age of
 * roughly HALF of all clients at any given moment: someone born in December 1990 reads as 36 from
 * 1 January, five months early. It is also the number that feeds nutrition/BMR conversations, so
 * it is not merely cosmetic.
 *
 * `dateOfBirth` crosses the wire as an epoch even though it is semantically a CALENDAR DAY, so the
 * comparison is done on local calendar parts — never by dividing an elapsed millisecond span,
 * which drifts across leap years and DST.
 */
export const calculateAge = (
  dateOfBirth?: number | Date | null,
): number | null => {
  if (dateOfBirth == null) return null;

  const dob = dateOfBirth instanceof Date ? dateOfBirth : new Date(dateOfBirth * 1000);
  if (Number.isNaN(dob.getTime())) return null;

  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();

  // Subtract a year when this year's birthday is still ahead of us. Comparing month-then-day
  // rather than a millisecond delta keeps leap days and DST out of the arithmetic.
  const monthDiff = now.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) {
    age -= 1;
  }

  // A future date of birth is bad data, not a negative age.
  return Math.max(age, 0);
};
